import {
  Box,
  NativeSelect,
  styled,
  Theme,
  Typography,
  useTheme
} from "@material-ui/core";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import { navigate } from "gatsby";
import React from "react";
import fastCompare from "react-fast-compare";
import { useToggle } from "react-use";
import ChevronDownIcon from "../../../../../components/icons/chevron-down";
import { addTrailingSlash } from "../../../../../helpers/path";
import { formatPrice } from "../../../../../helpers/price";
import {
  getDefaultProductVariant,
  getProductVariantWeightLabel
} from "../../../../../helpers/product";
import { getProductDetailsPath } from "../../../../../helpers/url";
import useFeatureFlag from "../../../../../hooks/use-feature-flag";
import useLocalizedLocationPath from "../../../../../hooks/use-localized-location-path";
import useTranslate from "../../../../../hooks/use-translate";
import useUnlocalizedLocationPath from "../../../../../hooks/use-unlocalized-location-path";
import { PriceType, ProductType } from "../../../../../types/airgraft";
import PodRating, { MIN_RATINGS_COUNT } from "../../../PodRating";
import { useProductVariantByQueryParam } from "../../utils";
import ProductVariantIcon from "./product-variant-icon.svg";
import {
  CustomVariantInput,
  IntentoryProgressBarLevels,
  InventoryProgressBar,
  InfoTooltip
} from "./styles";

export const MultiLineTypography = styled(Typography)(
  ({ theme }: { theme: Theme; component?: React.ElementType }) => ({
    maxWidth: "60px",
    fontWeight: 600,
    fontFamily: "Roobert, Arial",
    letterSpacing: "1px",
    fontSize: "1.2rem",
    lineHeight: "1.3rem",
    textTransform: "uppercase",
    whiteSpace: "pre-wrap"
  })
);

export const PriceTypography = styled(Typography)(
  ({ theme }: { theme: Theme; component?: React.ElementType }) => ({
    fontFamily: "Reckless, Georgia",
    fontSize: "3.3rem",
    lineHeight: "3.5rem"
  })
);

type Props = {
  product: ProductType;
  price: PriceType | false;
  inventoryLevel?: "LOW" | "MEDIUM" | "GOOD";
  disableVariantsSelector?: boolean;
};

function ProductDetailsOilTable({
  product,
  price,
  inventoryLevel,
  disableVariantsSelector
}: Props) {
  const theme = useTheme();
  const t = useTranslate();
  const currentPath = useUnlocalizedLocationPath();
  const getLocalizedPath = useLocalizedLocationPath();

  const isXpressEnabled = useFeatureFlag("SHOP_XPRESS");
  const isRetailProvidersEnabled = useFeatureFlag("SHOP_RETAIL_PROVIDERS");
  const isVariantsSelectorEnabled =
    useFeatureFlag("SHOP_PDP_VARIANTS_SELECTOR") && !disableVariantsSelector;
  const [productVariant, setProductVariantQueryParam] =
    useProductVariantByQueryParam(product);

  const oilDetails = productVariant.variantDetail;
  const coaReport = oilDetails?.coaReport;

  if (!oilDetails) {
    return null;
  }

  const productVariantWeightLabel =
    getProductVariantWeightLabel(productVariant);

  const handleVariantSelectorChange: React.ChangeEventHandler<
    HTMLSelectElement
  > = event => {
    const variantId = event.target.value;
    const productDetailsPagePath = addTrailingSlash(
      getProductDetailsPath(product.productType, product.slug)
    );
    const isDefaultVariant =
      variantId === getDefaultProductVariant(product)?.originalId;
    const isProductDetailsPage = currentPath === productDetailsPagePath;

    // On non-pdp pages: redirect to /shop/pods/{slug}/?variant={id}
    if (!isProductDetailsPage) {
      navigate(
        getLocalizedPath(`${productDetailsPagePath}?variant=${variantId}`)
      );
    }
    // On pdp page: set ?variant={id} query param
    else {
      setProductVariantQueryParam(
        isDefaultVariant ? undefined : event.target.value
      );
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="stretch"
      justifyContent="space-between"
      textAlign="left"
      whiteSpace="nowrap"
      bgcolor="grey.100"
      mb={3}
      borderRadius="8px"
    >
      {/* Row: Variant selector */}
      {isVariantsSelectorEnabled && product.variants.length > 1 && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          paddingY={1}
          paddingX={2}
          borderBottom={`1px solid ${theme.palette.grey[300]}`}
        >
          <Box flex={1}>
            <NativeSelect
              value={productVariant.originalId}
              onChange={handleVariantSelectorChange}
              IconComponent={ChevronDownIcon}
              startAdornment={<img src={ProductVariantIcon} />}
              input={
                <CustomVariantInput
                  name="product-variant"
                  id="product-variant-select"
                />
              }
            >
              {product.variants.map(variant => (
                <option key={variant.originalId} value={variant.originalId}>
                  {variant.variantTitle}
                </option>
              ))}
            </NativeSelect>
          </Box>

          {/* Variants info icon + tooltip */}
          <InfoTooltip
            arrow
            enterTouchDelay={0}
            interactive
            placement="bottom"
            title={
              <>
                <Box mb={0.5}>
                  <Typography className="compact" style={{ lineHeight: 1.5 }}>
                    {t("pdp.variantsInfo.title")}
                  </Typography>
                </Box>
                <Typography variant="caption">
                  {t("pdp.variantsInfo.text")}
                </Typography>
              </>
            }
          >
            <InfoOutlined style={{ fontSize: "2rem", cursor: "pointer" }} />
          </InfoTooltip>
        </Box>
      )}

      {/* First row */}
      <Box display="flex" paddingY={1} paddingX={2}>
        {/* Extraction type (oilType) */}
        <Box
          pr={1}
          flex={1}
          borderRight={`1px solid ${theme.palette.grey[300]}`}
          paddingY="2px"
        >
          <Typography variant="body2">
            {t(`oilType.${oilDetails.oilType}`)}
          </Typography>
        </Box>

        {/* Sativa/Indica/Hybrid */}
        <Box
          flex={1}
          paddingX={2}
          borderRight={`1px solid ${theme.palette.grey[300]}`}
          paddingY="2px"
        >
          <Typography variant="body2">
            {t(`plantSpecies.${oilDetails.plantSpecies}`)}
          </Typography>
        </Box>

        {/* Weight 1g */}
        {productVariantWeightLabel && (
          <Box paddingX={2} minWidth="50px" paddingY="2px">
            <Typography variant="body2">{productVariantWeightLabel}</Typography>
          </Box>
        )}

        {/* Rating */}
        {productVariant.rating &&
          productVariant.rating?.count >= MIN_RATINGS_COUNT && (
            <Box
              paddingLeft={2}
              minWidth="50px"
              paddingY="2px"
              borderLeft={`1px solid ${theme.palette.grey[300]}`}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              <PodRating rating={productVariant.rating} />
            </Box>
          )}
      </Box>

      {/* Second row THC/CBD/TERPS */}
      {coaReport && (
        <Box
          display="flex"
          paddingY={0.75}
          paddingX={2}
          borderTop={`1px solid ${theme.palette.grey[300]}`}
        >
          {/* THC */}
          <Box pr={1} flex={1}>
            <Typography variant="body2">
              THC: {coaReport.thcTotalPercentText}
            </Typography>
          </Box>

          {/* CBD/Secondary Cannabinoid */}
          <Box paddingX={1} flex={1}>
            <Typography variant="body2">
              {coaReport.secondaryCannabinoidLabel}:{" "}
              {coaReport.secondaryCannabinoidTotalPercentText}
            </Typography>
          </Box>

          {/* TERPS */}
          {coaReport.terpenesTotalPercentText && (
            <Box paddingLeft={1} textAlign="right">
              <Typography variant="body2">
                TERPS: {coaReport.terpenesTotalPercentText}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Second row (Fallback if no THC/CBD/TERPS info) */}
      {!coaReport && oilDetails.aromas && (
        <Box
          display="flex"
          paddingY={1}
          paddingX={2}
          borderTop={`1px solid ${theme.palette.grey[300]}`}
        >
          {/* Aromas */}
          <Box pr={1} flex={1}>
            <Typography variant="body2">
              {oilDetails.aromas.map(a => a.title).join(", ")}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Third row */}
      {!!price && isXpressEnabled && !isRetailProvidersEnabled && (
        <Box
          display="flex"
          paddingY={1}
          paddingX={2}
          borderTop={`1px solid ${theme.palette.grey[300]}`}
        >
          <Box
            pr={1}
            flex={1}
            borderRight={`1px solid ${theme.palette.grey[300]}`}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            paddingRight={2}
          >
            <Typography variant="body2" style={{ whiteSpace: "break-spaces" }}>
              {productVariant.variantDetail?.effects
                .slice(0, 4)
                .map(e => e.name)
                .join(", ")}
            </Typography>
          </Box>

          {/* Price */}
          <Box
            paddingLeft={2}
            flex={1}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
          >
            <PriceTypography>
              {formatPrice(price, {
                hideCurrencyCode: true
              })}
            </PriceTypography>
          </Box>
        </Box>
      )}

      {/* Inventory levels */}
      {inventoryLevel && inventoryLevel !== "GOOD" && (
        <Box
          display="flex"
          paddingY={1}
          paddingX={2}
          borderTop={`1px solid ${theme.palette.grey[300]}`}
          alignItems="center"
          justifyContent="space-between"
        >
          <InventoryProgressBar
            value={IntentoryProgressBarLevels[inventoryLevel]}
            inventoryLevel={inventoryLevel}
          />
          <Typography className="compact">
            {inventoryLevel === "LOW" && "Almost sold out"}
            {inventoryLevel === "MEDIUM" && "Stock is running low"}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default React.memo(ProductDetailsOilTable, fastCompare);
